<template>
  <v-form class="mb-4">
    <v-row class="mb-4" dense>
      <v-col
        cols="12"
        sm="6"
        :class="{ correction: correction.correctionType != undefined }"
      >
        <v-select
          :items="receiptTypes"
          item-text="name"
          item-value="id"
          v-model="order.receipt.receiptType"
          label="Тип чека"
          :disabled="disabled"
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        :class="{ correction: correction.correctionType != undefined }"
      >
        <v-select
          :items="correctionTypes"
          item-text="name"
          item-value="id"
          v-model="correction.correctionType"
          label="Чек коррекции"
          placeholder="нет"
          clearable
          :disabled="disabled || noCorrection"
        />
      </v-col>
      <v-col
        cols="12"
        v-if="correction.correctionType != undefined"
        class="correction"
      >
        <v-container class="pa-0" fluid>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                label="Причина коррекции"
                v-model="correction.causeName"
                :rules="rules['receipt.correction.causeName']"
                :disabled="disabled"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="4">
              <date-field
                label="Дата коррекции"
                v-model="correction.causeDocumentDate"
                clearable
                :max="today"
                :rules="rules['receipt.correction.causeDocumentDate']"
                :disabled="disabled"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :label="
                  correction.correctionType === 1
                    ? 'Номер предписания'
                    : 'Номер внутр. документа'
                "
                v-model="correction.causeDocumentNumber"
                :rules="rules['receipt.correction.causeDocumentNumber']"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col cols="12" sm="6">
        <reference-field
          label="Группа касс"
          reference="group"
          item-text="nameWithCompany"
          :rels="['company']"
          :rules="rules.groupId"
          v-model="order.groupId"
          :filter="clientFilter"
          @onselect="onGroupSelect"
          default-one
          :disabled="disabled || noCorrection"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <reference-field
          label="Кассир"
          reference="cashier"
          v-model="order.cashierId"
          @onselect="onCashierSelect"
          :rules="rules.cashierId"
          :filter="clientFilter"
          default-one
          :disabled="disabled"
        />
      </v-col>
      <v-col cols="7" sm="6">
        <v-text-field
          label="Должность и фамилия кассира"
          v-model="order.receipt.cashierName"
          :rules="rules['receipt.cashierName']"
          :disabled="disabled"
          clearable
          :placeholder="cashier && cashier.nameWithTitle"
          hint='должность "кассир" можно не указывать'
        />
      </v-col>
      <v-col cols="5" sm="6">
        <v-text-field
          label="ИНН кассира (опционально)"
          v-model="order.receipt.cashierINN"
          :rules="rules['receipt.cashierINN']"
          :disabled="disabled"
          clearable
          :placeholder="cashier && cashier.inn"
          hint="не обязательно"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          :items="taxSystems"
          item-text="name"
          item-value="id"
          v-model="order.receipt.taxSystem"
          label="Система налогообложения"
          placeholder="по-умолчанию"
          :disabled="disabled"
        />
      </v-col>
      <AgentFields
        v-model="agent"
        :disabled="disabled"
        :rules="rules"
        rules-prefix="receipt.agent."
      />
      <v-col cols="7" sm="6">
        <v-text-field
          label="Данные покупателя (опционально)"
          v-model="order.receipt.customerName"
          :rules="rules['receipt.customerName']"
          :disabled="disabled"
          hint="Фамилия, номер паспорта"
        />
      </v-col>
      <v-col cols="5" sm="6">
        <v-text-field
          label="ИНН покупателя (опционально)"
          v-model="order.receipt.customerINN"
          :rules="rules['receipt.customerINN']"
          :disabled="disabled"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          label="Отправить чек покупателю"
          :value="this.maskedCustomerContact"
          @change="onContactInput"
          :rules="rules['receipt.customerContact']"
          :disabled="disabled"
          clearable
          hint="email или номер телефона"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          label="Место расчета"
          placeholder="по-умолчанию"
          v-model="order.receipt.place"
          :rules="rules['receipt.place']"
          :disabled="disabled"
          clearable
        />
      </v-col>
    </v-row>
    <div class="pt-2 subtitle-1">
      ПРЕДМЕТЫ РАСЧЕТА
    </div>
    <v-list two-line subheader>
      <template v-for="(item, index) in order.receipt.products">
        <v-list-item
          :key="index"
          @click="editProduct(index)"
          :disabled="disabled"
          class="px-0"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-action-text>
              {{ getPaymentMethodName(item.paymentMethod || 4) }}
            </v-list-item-action-text>
          </v-list-item-content>
          <v-list-item-action>
            <v-list-item-title>
              {{ item.price | currency }} x {{ item.quantity / 1000 }} =
              {{ ((item.price * item.quantity) / 1000) | currency }}
            </v-list-item-title>
            <v-list-item-action-text>
              {{ getTaxName(item.taxType) }}
            </v-list-item-action-text>
          </v-list-item-action>
        </v-list-item>
        <v-divider :key="'divider' + index" />
      </template>
    </v-list>
    <v-row>
      <v-col class="grow py-0">
        <v-btn
          text
          small
          color="primary"
          class="mt-2 px-0"
          @click="addProduct"
          :disabled="disabled"
        >
          <v-icon small>add</v-icon>
          добавить предмет расчета
        </v-btn>
      </v-col>
      <v-col class="shrink py-0">
        <DeliveryForm
          v-on:add-delivery="addDelivery"
          :value="order.delivery"
          v-if="!order.receipt.correction && noCorrection !== ''"
        />
      </v-col>
    </v-row>
    <div v-if="order.amount > 0" class="my-5 pt-4">
      <div class="subtitle-1">ИТОГ</div>
      <div class="py-2">
        <PaymentForm
          v-model="order.receipt.payment"
          :amount="order.amount"
          :disabled="disabled"
        />
      </div>
    </div>
    <v-dialog v-model="showProductEdit" max-width="640" v-if="product">
      <v-card>
        <v-card-title>Предмет расчета</v-card-title>
        <v-card-text>
          <ProductFields
            v-model="product"
            :rules="productRules"
            :force-tax-type="group && group.forceTaxType"
            :clientId="clientId"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="green"
            :disabled="!isValidProduct"
            :dark="isValidProduct"
            @click="saveProduct"
            >Сохранить</v-btn
          >
          <v-btn text @click="product = null">Отмена</v-btn>
          <v-spacer />
          <v-btn
            text
            color="error"
            v-if="productIndex > 0"
            @click="removeProduct"
            >Удалить</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>
<script>
import ds from "@/datastore";
import { buildRules } from "@/datastore/rules";
import ProductFields from "@/components/ui/forms/ProductFields";
import {
  ReceiptTypes,
  TaxSystems,
  PaymentTypes,
  CorrectionTypes,
  getTaxName,
  getPaymentMethodName
} from "@/datastore/enums";
import cloneDeep from "lodash.clonedeep";
import PaymentForm from "./PaymentForm";
import authMixin from "@/mixins/auth";
import { normPhoneNumber, trimPhoneNumber } from "@/lib/phone";
import DeliveryForm from "./DeliveryForm";
import AgentFields from "./AgentFields";
import { unsetEmptyStrings } from "@/datastore/record";
export default {
  name: "OrderForm",
  mixins: [authMixin],
  components: { ProductFields, PaymentForm, DeliveryForm, AgentFields },
  props: ["value", "disabled", "noCorrection", "clientId"],
  data() {
    return {
      order: this.value,
      group: null,
      cashier: null,
      correctionTypes: CorrectionTypes,
      paymentTypes: PaymentTypes,
      paymentType: "cash",
      product: null,
      productIndex: -1,
      agent: this.value.receipt.agent || {},
      correction: this.value.receipt.correction || {
        causeName: "Неприменение ККТ"
      },
      today: "now",
      maskedCustomerContact: ""
    };
  },
  async created() {
    this.onContactInput(this.value.receipt.customerContact || "");
    if (this.order.groupId) {
      this.group = await ds.find("group", this.order.groupId);
      if (this.group && this.order.receipt.taxSystem === null) {
        this.order.receipt.taxSystem = this.group.taxSystem;
      }
    }
  },
  mounted() {
    this.value.receipt.products || this.addProduct();
  },
  watch: {
    "agent.agentType": function(v) {
      if (!v) {
        this.order.receipt.agent = undefined;
      } else {
        this.order.receipt.agent = this.agent;
      }
    },
    "correction.correctionType": function(v) {
      if (v === undefined) {
        this.order.receipt.correction = undefined;
      } else {
        this.today = new Date().toISOString().substr(0, 10);
        this.correction.causeDocumentDate = this.today;
        this.order.receipt.correction = this.correction;
      }
    }
  },
  computed: {
    clientFilter() {
      return this.clientId ? { clientId: this.clientId } : {};
    },
    receiptTypes() {
      if (!this.order.receipt.correction) {
        return ReceiptTypes;
      }
      return ReceiptTypes.filter(t => [1, 3].includes(t["id"])).map(t => {
        return {
          id: t.id,
          name: t.name + " (чек коррекции)"
        };
      });
    },
    taxSystems() {
      return TaxSystems;
    },
    rules() {
      return (this.order && this.order.$rules) || {};
    },
    showProductEdit: {
      get() {
        return this.product;
      },
      set(v) {
        !v && (this.product = null);
      }
    },
    productErrors() {
      false && unsetEmptyStrings(this.product);
      return (
        (this.product &&
          this.order
            ._mapper()
            .schema.properties.receipt.properties.products.items.validate(
              this.product
            )) ||
        []
      );
    },
    productRules() {
      return buildRules(this.productErrors);
    },
    isValidProduct() {
      return this.productErrors.length === 0;
    }
  },
  methods: {
    getTaxName,
    getPaymentMethodName,
    onGroupSelect(v) {
      this.group = v;
      if (v && v.taxSystem != null) {
        this.order.receipt.taxSystem = v.taxSystem;
      }
    },
    onCashierSelect(v) {
      this.cashier = v;
      if (v) {
        if (!this.order.groupId) {
          this.order.groupId = v.groupId;
        }
        if (!this.order.receipt.place) {
          this.order.receipt.place = this.cashier.place;
        }
        this.order.receipt.cashierName = "";
        this.order.receipt.cashierINN = "";
      } else {
        //this.order.receipt.cashierName = this.auth.user.name;
        this.order.receipt.cashierName = "";
        this.order.receipt.cashierINN = "";
      }
    },
    onContactInput(value) {
      let phone =
        !value.includes("@") &&
        trimPhoneNumber(normPhoneNumber(value)).substr(0, 10);
      if (phone) {
        phone = "+7" + phone;
        this.maskedCustomerContact = this.$options.filters.phone(phone);
        this.order.receipt.customerContact = phone;
      } else {
        this.order.receipt.customerContact = value.trim();
      }
    },
    addProduct() {
      this.order.receipt.products || (this.order.receipt.products = []);
      this.product = {
        productId: undefined,
        //name: undefined,
        price: undefined,
        quantity: 1000,
        taxType: undefined,
        productType: undefined,
        paymentMethod: undefined,
        productCode: undefined,
        agent: undefined
      };
      this.productIndex = this.order.receipt.products.length;
      if (this.productIndex > 0) {
        const p = this.order.receipt.products[
          this.order.receipt.products.length - 1
        ];
        this.product.paymentMethod = p.paymentMethod;
        this.product.taxType = p.taxType;
        this.product.productType = p.productType;
      }
      if (this.group && this.group.forceTaxType != null) {
        this.product.taxType = this.group.forceTaxType;
      }
    },
    editProduct(index) {
      this.product = cloneDeep(this.order.receipt.products[index]);
      this.productIndex = index;
    },
    saveProduct() {
      this.order.receipt.products[this.productIndex] = this.product;
      this.product = null;
    },
    removeProduct() {
      this.order.receipt.products.splice(this.productIndex, 1);
      this.product = null;
    },
    addDelivery(d) {
      this.order.delivery = d;
    }
  }
};
</script>
<style scoped>
.correction {
  background-color: #fff8f8;
}
</style>
